/**
 * API response contract for BNI QRIS Admin Portal.
 *
 * Source: the "Standarisasi Response API" document by Zakky Supriyadi and
 * I Nyoman Arijaya Putra. Every status number and legacy code mapping lives
 * here so that `code == 200` comparisons are no longer scattered across
 * store modules.
 */

/**
 * HTTP status codes defined by the contract.
 *
 * @readonly
 * @enum {number}
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  EMPTY: 204,
  BAD_REQUEST: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE: 422,
  SERVER_ERROR: 500,
};

/**
 * Failure classification used by the UI to decide what to do next.
 *
 * The `network`, `timeout`, and `canceled` kinds do not originate from the
 * server; they describe requests that failed before a response arrived.
 *
 * @readonly
 * @enum {string}
 */
export const ERROR_KIND = {
  BAD_REQUEST: 'bad_request',
  UNAUTHENTICATED: 'unauthenticated',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  CONFLICT: 'conflict',
  VALIDATION: 'validation',
  SERVER: 'server',
  NETWORK: 'network',
  TIMEOUT: 'timeout',
  CANCELED: 'canceled',
  UNKNOWN: 'unknown',
};

const KIND_BY_STATUS = {
  [HTTP_STATUS.BAD_REQUEST]: ERROR_KIND.BAD_REQUEST,
  [HTTP_STATUS.UNAUTHENTICATED]: ERROR_KIND.UNAUTHENTICATED,
  [HTTP_STATUS.FORBIDDEN]: ERROR_KIND.FORBIDDEN,
  [HTTP_STATUS.NOT_FOUND]: ERROR_KIND.NOT_FOUND,
  [HTTP_STATUS.CONFLICT]: ERROR_KIND.CONFLICT,
  [HTTP_STATUS.UNPROCESSABLE]: ERROR_KIND.VALIDATION,
  [HTTP_STATUS.SERVER_ERROR]: ERROR_KIND.SERVER,
};

/**
 * Translates a contract code into a failure classification.
 *
 * @param {number} code - Code taken from the response envelope.
 * @returns {string} One of the {@link ERROR_KIND} values.
 */
export const kindOf = (code) => {
  if (KIND_BY_STATUS[code]) return KIND_BY_STATUS[code];
  if (code >= 500) return ERROR_KIND.SERVER;
  if (code >= 400) return ERROR_KIND.BAD_REQUEST;
  return ERROR_KIND.UNKNOWN;
};

/**
 * Reports whether a code means success.
 *
 * The whole 2xx range counts, not just the two statuses the contract spells
 * out. A create endpoint answering 201 is a success as much as a 200 is, and
 * treating it as a failure would tell the user their record was rejected after
 * the server had already stored it.
 *
 * @param {number} code - Code taken from the response envelope.
 * @returns {boolean} True for any 2xx code.
 */
export const isSuccessCode = (code) => code >= HTTP_STATUS.OK && code < 300;

/**
 * Success codes used by the legacy response format.
 *
 * The mix of numbers and strings comes from the old `postApi` allowlist and
 * from switching endpoints that pass their own response codes through.
 * Values are compared as strings so that `200` and `'200'` both match.
 *
 * @type {Set<string>}
 */
const LEGACY_SUCCESS_CODES = new Set(['200', '00', '01', '10', '11', 'A0']);

/**
 * Legacy codes that mean success but still require the UI to branch, such as
 * the login flow asking for an OTP or forcing a password change.
 *
 * These are passed through unchanged on the `legacyCode` property.
 *
 * @type {Set<string>}
 */
const LEGACY_STATEFUL_SUCCESS_CODES = new Set(['202', '205', '206']);

/**
 * Reports whether a legacy code means success.
 *
 * @param {*} code - Raw code from a legacy response body.
 * @returns {boolean} True when the request succeeded.
 */
export const isLegacySuccessCode = (code) => {
  const raw = String(code ?? '').toUpperCase();
  return LEGACY_SUCCESS_CODES.has(raw) || LEGACY_STATEFUL_SUCCESS_CODES.has(raw);
};

/**
 * Maps a legacy response code onto the current contract.
 *
 * Used while some endpoints already follow the new contract and others do not.
 * A body code that names a specific condition is honoured even when the
 * transport status disagrees, because legacy endpoints routinely answer with
 * HTTP 200 while reporting the real outcome in the body.
 *
 * Codes 302, 300, and 2 are generic failure codes that carry no meaning of their
 * own. For those the transport status decides whenever it is itself an error,
 * so that an expired session answered as HTTP 401 with a body code of 302 is
 * still recognised as an authentication failure rather than a plain bad request.
 * Only when the transport reports success does the shape of the payload decide
 * between a validation failure and a generic rejection.
 *
 * The original value is preserved on the `legacyCode` property of the envelope.
 *
 * @param {*} code - Raw code from a legacy response body.
 * @param {boolean} hasFieldErrors - Whether the payload carries per-field errors.
 * @param {number} [httpStatus=200] - Actual HTTP status of the response.
 * @returns {number} The equivalent contract code.
 */
export const mapLegacyCode = (code, hasFieldErrors, httpStatus = HTTP_STATUS.OK) => {
  if (isLegacySuccessCode(code)) return HTTP_STATUS.OK;

  switch (String(code ?? '').toUpperCase()) {
    case '401':
      return HTTP_STATUS.UNAUTHENTICATED;
    case '403':
      return HTTP_STATUS.FORBIDDEN;
    case '404':
      return HTTP_STATUS.NOT_FOUND;
    case '409':
      return HTTP_STATUS.CONFLICT;
    case '422':
      return HTTP_STATUS.UNPROCESSABLE;
    case '500':
      return HTTP_STATUS.SERVER_ERROR;
    default:
      if (httpStatus >= HTTP_STATUS.BAD_REQUEST) return httpStatus;
      return hasFieldErrors
        ? HTTP_STATUS.UNPROCESSABLE
        : HTTP_STATUS.BAD_REQUEST;
  }
};
